/**
 * Shared Product · GI Presenze — Desktop Notifications (categorie di prodotto)
 *
 * Wrapper Product-specific attorno alle utility CORE
 * (`@/foundation/notifications`). Contiene:
 * - le CATEGORIE di dominio GI Presenze (leave_requests, safety [D.Lgs. 81/2008],
 *   attendance, counters, profile)
 * - il MAPPING kind→categoria specifico di GI Presenze
 * - le CHIAVI localStorage prefissate `presenze_*` (namespace del Product Pack)
 * - il titolo/icona di default del Product Pack ("Gestionale Presenze", "/logo192.png")
 *
 * Ogni Product Pack diverso (GI Clienti, GI Studio, ...) creera' il proprio wrapper
 * analogo, senza toccare il Foundation.
 */
import {
  isDesktopNotificationSupported,
  getDesktopPermission,
  requestDesktopPermission,
  fireNotification,
  createLocalStorageToggle,
} from "@/foundation/notifications";

// Re-export utility CORE (retrocompatibilita' consumer legacy).
export { isDesktopNotificationSupported, getDesktopPermission, requestDesktopPermission };

// LocalStorage namespace — SSoT del Product Pack GI Presenze.
const LS_KEY = "presenze_desktop_notifications_enabled";
const LS_CATEGORIES = "presenze_desktop_notifications_categories";
const DEFAULT_TITLE = "Gestionale Presenze";
const DEFAULT_ICON = "/logo192.png";
const noop = () => {};

// Toggle CORE parametrizzato sul namespace GI Presenze.
const _toggle = createLocalStorageToggle(LS_KEY);

/**
 * Mappa "kind" del backend → categoria macroscopica visibile in UI.
 * Le chiavi devono restare stabili: sono salvate in localStorage.
 */
export const NOTIFICATION_CATEGORIES = {
  leave_requests: {
    label: "Richieste ferie e permessi",
    description: "Nuove richieste, approvazioni e rifiuti da parte di dipendenti e responsabili.",
    kinds: ["leave_request_submitted", "leave_request_approved", "leave_request_rejected"],
    defaultOn: true,
  },
  safety: {
    label: "Scadenze D.Lgs. 81/2008",
    description: "Visite mediche, formazione e certificazioni in scadenza o già scadute.",
    kinds: ["safety_expiring", "safety_expired"],
    defaultOn: true,
  },
  attendance: {
    label: "Presenze e workflow mensile",
    description: "Chiusure mensili, presenze inviate dai dipendenti e cambi di stato.",
    kinds: ["attendance_submitted", "attendance_workflow_changed", "attendance_month_closed_external"],
    defaultOn: true,
  },
  counters: {
    label: "Contatori e ratei",
    description: "Anomalie di saldo e ratei ferie/ROL/ex festività in esaurimento.",
    kinds: ["balance_anomaly", "ratei_ferie_alert", "ratei_rol_alert", "ratei_ex_fest_alert"],
    defaultOn: true,
  },
  profile: {
    label: "Anagrafica dipendente",
    description: "Aggiornamenti al profilo del dipendente.",
    kinds: ["employee_profile_updated"],
    defaultOn: false,
  },
};

/** Categoria di fallback per kind non mappati (default: attive). */
const OTHER_CATEGORY = "other";

/** Flag opt-in complessivo (localStorage + permission API). */
export function getDesktopEnabled() {
  if (!isDesktopNotificationSupported()) return false;
  return _toggle.getEnabled() && Notification.permission === "granted";
}

export function setDesktopEnabled(enabled) {
  if (!isDesktopNotificationSupported()) return;
  _toggle.setEnabled(enabled);
}

/* ---------------------------------------------------------------------- */
/*  Preferenze per categoria                                              */
/* ---------------------------------------------------------------------- */

function _defaultPrefs() {
  const out = { [OTHER_CATEGORY]: true };
  for (const [key, cfg] of Object.entries(NOTIFICATION_CATEGORIES)) {
    out[key] = cfg.defaultOn;
  }
  return out;
}

export function getCategoryPreferences() {
  const def = _defaultPrefs();
  try {
    const raw = localStorage.getItem(LS_CATEGORIES);
    if (!raw) return def;
    const parsed = JSON.parse(raw);
    return { ...def, ...(parsed || {}) };
  } catch (_e) { return def; }
}

export function setCategoryPreference(categoryKey, enabled) {
  const prefs = getCategoryPreferences();
  prefs[categoryKey] = !!enabled;
  try { localStorage.setItem(LS_CATEGORIES, JSON.stringify(prefs)); }
  catch (_e) { noop(); }
}

/** Restituisce la chiave di categoria per un `kind` GI Presenze. */
export function categoryForKind(kind) {
  if (!kind) return OTHER_CATEGORY;
  for (const [key, cfg] of Object.entries(NOTIFICATION_CATEGORIES)) {
    if (cfg.kinds.includes(kind)) return key;
  }
  return OTHER_CATEGORY;
}

/** True se le notifiche desktop sono attive E la categoria e' abilitata. */
export function isKindAllowed(kind) {
  if (!getDesktopEnabled()) return false;
  const key = categoryForKind(kind);
  return getCategoryPreferences()[key] !== false;
}

/* ---------------------------------------------------------------------- */
/*  Emissione notifiche · delega al Foundation                            */
/* ---------------------------------------------------------------------- */

/**
 * Crea una notifica desktop se abilitate e se la categoria del `kind` e'
 * consentita. Ritorna true se la notifica e' stata effettivamente creata.
 *
 * La logica di gating (opt-in + category) resta Shared Product;
 * l'emissione tecnica delega a `fireNotification` del Foundation.
 */
export function fireDesktopNotification({ title, body, tag, onClick, icon, kind }) {
  if (!isKindAllowed(kind)) return false;
  return fireNotification({
    title: title || DEFAULT_TITLE,
    body,
    tag,
    onClick,
    icon: icon || DEFAULT_ICON,
    badge: DEFAULT_ICON,
  });
}
